import React, { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Grid,
    ListItem,
    List,
    ListItemText,
    Card,
    CardContent,
    CardActions,
    Chip,
    Box,
    useTheme,
    useMediaQuery,
    Fade,
    Tooltip,
    IconButton,
    TextField,
    InputAdornment
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { deleteAssignment, getAssessmentResults, getAssignments, getUserAssessmentResults } from '../Queries';
import { getFilterProgramCodes } from '../../utils/clientLogoService';
import LoadingScreen from '../UI/LoadingScreen';
import { Edit, EditNote, Preview, ViewList, Assignment, CheckCircle, Schedule, Search, Clear } from '@mui/icons-material';
import AssessmentPreview from './AssessmentPreview';
import UserAssessmentForm from './UserAssessmentForm';
import AssessmentSubmissions from './AssessmentSubmissions';
import AssessmentTableCommon from './AssessmentTableCommon';

const AssessmentTable = ({ onEdit }) => {
    const [assessments, setAssessments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedAssessment, setSelectedAssessment] = useState(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [takeAssessmentOpen, setTakeAssessmentOpen] = useState(false);
    const [isFormAlreadySubmitted, setIsFormAlreadySubmitted] = useState(false);
    const [isSubmissionsOpen, setIsSubmissionsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

    let [userData] = useState(localStorage.getItem("userData"));
    userData = userData ? JSON.parse(userData) : userData;

    const handlePreviewOpen = (assessment) => {
        setSelectedAssessment(assessment);
        setIsPreviewOpen(true);
    };

    const handleTakeAssessment = (assessment) => {
        if (assessment && assessment.assessmentResults.length > 0) {
            // let jsonData = JSON.parse(assessment.assessmentResults.json);
            // console.log("Assessment JSON Data:", jsonData);
            // jsonData.map(item => {
            //     if (item.user_id === userData.email) {
            setIsFormAlreadySubmitted(true);
            //     }
            //     return null;
            // })
        } else {
            setIsFormAlreadySubmitted(false);
        }
        setSelectedAssessment(assessment);
        setTakeAssessmentOpen(true);
    };

    const handlePreviewClose = () => {
        setIsPreviewOpen(false);
    };

    const handleTakeAssessmentClose = () => {
        // Only refresh data for users to get updated assessment results
        if (userData.user_role === "User") {
            getAssessmentData();
        }
        setIsFormAlreadySubmitted(false);
        setTakeAssessmentOpen(false);
    };

    const handleViewSubmissions = async (assessment) => {
        // Open dialog immediately with loading state
        setSelectedAssessment({ ...assessment, isLoadingSubmissions: true });
        setIsSubmissionsOpen(true);
        
        try {
            // Fetch assessment results only when viewing submissions
            const allAssessmentResults = await getAssessmentResults(assessment.form_id);
            
            // Create updated assessment with results
            const updatedAssessment = {
                ...assessment,
                assessmentResults: allAssessmentResults,
                json: allAssessmentResults.length > 0 ? JSON.stringify(allAssessmentResults) : assessment.json,
                isLoadingSubmissions: false
            };
            
            setSelectedAssessment(updatedAssessment);
        } catch (error) {
            console.error('Error fetching assessment results:', error);
            // Update with error state but keep dialog open
            setSelectedAssessment({
                ...assessment,
                assessmentResults: [],
                isLoadingSubmissions: false,
                submissionsError: true
            });
        }
    };

    const handleSubmissionsClose = () => {
        setIsSubmissionsOpen(false);
    };
    const getAssessmentData = async () => {
        try {
            setIsLoading(true);
            const assignmentsData = await getAssignments();

            // For User role, get the filtered program codes based on client check
            let allowedProgramCodes = [];
            if (userData.user_role === "User") {
                const userProgramCodes = userData.programCode || userData.user_batch;
                console.log('[AssessmentTable] User program codes:', userProgramCodes);
                
                allowedProgramCodes = await getFilterProgramCodes(userProgramCodes);
                console.log('[AssessmentTable] Filtered program codes for assessments:', allowedProgramCodes);
            }

            const filteredAssignments = await Promise.all(
                assignmentsData.map(async (assignmentItem) => {
                    if (assignmentItem.assignment_topic !== "assessment") return null;

                    if (userData.user_role === "User") {
                        // Check if the assessment's program_code matches any of the allowed program codes
                        const hasMatchingProgramCode = allowedProgramCodes.some(programCode => 
                            assignmentItem.program_code.includes(programCode)
                        );

                        if (hasMatchingProgramCode) {
                            // Get user-specific assessment results
                            const userAssessmentResults = await getUserAssessmentResults(assignmentItem.form_id, userData.email);

                            // Attach the results to the assignment object
                            assignmentItem.assessmentResults = userAssessmentResults;

                            // Update the json field to include user results for consistency
                            if (userAssessmentResults && userAssessmentResults.length > 0) {
                                assignmentItem.json = JSON.stringify(userAssessmentResults);
                            }

                            return assignmentItem;
                        } else {
                            return null;
                        }
                    }

                    if (userData.user_role === "Mentor") {
                        // Don't fetch assessment results here - only fetch when viewing submissions
                        assignmentItem.assessmentResults = [];
                        
                        return assignmentItem;
                    }

                    return null;
                })
            );
            // Remove null entries
            const finalAssignments = filteredAssignments.filter(item => item !== null);
            setAssessments(finalAssignments);

            // Update selectedAssessment if it exists and submissions dialog is open
            if (selectedAssessment && isSubmissionsOpen) {
                const updatedAssessment = finalAssignments.find(
                    assessment => assessment.form_id === selectedAssessment.form_id
                );
                if (updatedAssessment) {
                    setSelectedAssessment(updatedAssessment);
                }
            }

        } catch (error) {
            console.error('An error occurred while fetching assignments data.', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteAssignment = async (id) => {
        try {
            const confirmed = window.confirm("Are you sure you want to delete this assignment?");
            if (confirmed) {
                setIsLoading(true);
                const assignment = await deleteAssignment(id);
                getAssessmentData();
            }
        } catch (error) {
            console.error('Error deleting assignment:', error);
        }
    };

    const handleEdit = (assessment) => {
        onEdit(1, assessment);
    }

    // Filter assessments based on search term
    const filteredAssessments = assessments.filter(assessment => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        return (
            assessment.assignment_name.toLowerCase().includes(searchLower) ||
            assessment.assignment_type.toLowerCase().includes(searchLower) ||
            (assessment.program_code && assessment.program_code.toLowerCase().includes(searchLower))
        );
    });

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const isAssessmentSubmitted = (assessment) => {
        if (!assessment?.assessmentResults || !assessment.assessmentResults.length) return false;

        try {
            const parsed = assessment.assessmentResults;
            return parsed.some(item => item.user_id === userData.email);
        } catch (err) {
            console.error("Invalid JSON in assessment:", err);
            return false;
        }
    };

    // Get submission date for user
    const getSubmissionDate = (assessment) => {
        if (!assessment?.assessmentResults || !assessment.assessmentResults.length) return null;

        try {
            const parsed = assessment.assessmentResults;
            const userSubmission = parsed.find(item => item.user_id === userData.email);
            return userSubmission ? userSubmission.submitted_at || userSubmission.created_at : null;
        } catch (err) {
            console.error("Error getting submission date:", err);
            return null;
        }
    };

    // Get start and end dates from assessment
    const getAssessmentDates = (assessment) => {
        let startDate = null;
        let endDate = null;
        
        // Try to get from JSON field first (new format)
        if (assessment.json) {
            try {
                const jsonData = typeof assessment.json === 'string' ? JSON.parse(assessment.json) : assessment.json;
                if (jsonData.start_date) startDate = jsonData.start_date;
                if (jsonData.end_date) endDate = jsonData.end_date;
            } catch (error) {
                console.log('Error parsing assessment JSON:', error);
            }
        }
        
        // Fallback to direct fields if they exist
        if (!startDate && assessment.start_date) startDate = assessment.start_date;
        if (!endDate && assessment.end_date) endDate = assessment.end_date;
        
        return { startDate, endDate };
    };

    // Get assessment status based on current date and start/end dates
    const getAssessmentStatus = (assessment) => {
        const { startDate, endDate } = getAssessmentDates(assessment);
        
        if (!startDate || !endDate) {
            return { status: 'available', color: 'success', label: 'Available' };
        }
        
        const now = new Date();
        const start = new Date(startDate);
        const end = new Date(endDate);
        
        if (now < start) {
            return { status: 'upcoming', color: 'warning', label: 'Upcoming' };
        } else if (now > end) {
            return { status: 'closed', color: 'error', label: 'Closed' };
        } else {
            return { status: 'available', color: 'success', label: 'Available' };
        }
    };

    // Get assessment period display text
    const getDurationDisplay = (assessment) => {
        const { startDate, endDate } = getAssessmentDates(assessment);
        
        if (startDate && endDate) {
            return `${formatDate(startDate)} - ${formatDate(endDate)}`;
        }
        
        // If only start date is available
        if (startDate) {
            return `From: ${formatDate(startDate)}`;
        }
        
        // If only end date is available
        if (endDate) {
            return `Until: ${formatDate(endDate)}`;
        }
        
        // If no assessment period is set, show that it's not scheduled
        return "Not scheduled";
    };

    // Get the appropriate date to display
    const getDisplayDate = (assessment) => {
        if (userData.user_role === "User" && isAssessmentSubmitted(assessment)) {
            const submissionDate = getSubmissionDate(assessment);
            return submissionDate ? formatDate(submissionDate) : formatDate(assessment.created_at);
        }
        return formatDate(assessment.created_at);
    };

    // Get the appropriate date column header
    const getDateColumnHeader = () => {
        return "Assessment Period";
    };
    
    // Get submission info for users
    const getSubmissionInfo = () => {
        return "Submission Status";
    };

    const formatDate = (dateString) => {
        // Handle DD/MM/YYYY format
        if (dateString.includes('/')) {
            const [day, month, year] = dateString.split('/');
            const date = new Date(year, month - 1, day); // month is 0-indexed
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        }
        // Fallback for other formats
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    useEffect(() => {
        getAssessmentData();
    }, []);

    // Mobile Card View Component
    const MobileAssessmentCard = ({ assessment, index }) => (
        <Fade in timeout={300 + index * 100}>
            <Card
                sx={{
                    mb: 2,
                    borderRadius: 3,
                    // boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        // boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                        transform: 'translateY(-2px)'
                    },
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)'
                }}
            >
                <CardContent sx={{ pb: 1 }}>
                    <Box display="flex" alignItems="center" mb={2}>
                        <Assignment sx={{ color: '#0a0068', mr: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#0a0068', flex: 1 }}>
                            {assessment.assignment_name}
                        </Typography>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                            {isAssessmentSubmitted(assessment) && (
                                <Chip
                                    icon={<CheckCircle />}
                                    label="Completed"
                                    color="success"
                                    size="small"
                                    sx={{ fontWeight: 500 }}
                                />
                            )}
                            <Chip
                                label={getAssessmentStatus(assessment).label}
                                color={getAssessmentStatus(assessment).color}
                                size="small"
                                sx={{ fontWeight: 500 }}
                            />
                        </Box>
                    </Box>

                    {userData.user_role !== "User" && (
                        <>
                            <Box mb={1}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>
                                    Assigned To:
                                </Typography>
                                <Box display="flex" flexDirection="column" gap={0.5}>
                                    {assessment.program_code.split(',').map((code, index) => (
                                        <Chip 
                                            key={index}
                                            label={code.trim()} 
                                            size="small" 
                                            variant="outlined"
                                            sx={{ alignSelf: 'flex-start', fontSize: '0.7rem' }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                            <Box display="flex" justifyContent="space-between" mb={1}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                    Template:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {assessment.assignment_type}
                                </Typography>
                            </Box>
                        </>
                    )}

                    <Box mb={2}>
                        <Box display="flex" alignItems="center" mb={1}>
                            <Schedule sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Assessment Period:
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ ml: 2.5, fontWeight: 500 }}>
                            {getDurationDisplay(assessment)}
                        </Typography>
                        {userData.user_role === "User" && (
                            <Box sx={{ mt: 1, ml: 2.5 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                                    Submission:
                                </Typography>
                                {isAssessmentSubmitted(assessment) ? (
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#2e7d32', fontSize: '0.75rem' }}>
                                        Submitted on {getDisplayDate(assessment)}
                                    </Typography>
                                ) : (
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#ff9800', fontWeight: 500 }}>
                                        Not Submitted
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>
                </CardContent>

                <CardActions sx={{ pt: 0, px: 2, pb: 2 }}>
                    <Box display="flex" flexWrap="wrap" gap={1} width="100%">
                        {userData.user_role === "User" ? (
                            (() => {
                                const status = getAssessmentStatus(assessment);
                                const isSubmitted = isAssessmentSubmitted(assessment);
                                const isAvailable = status.status === 'available' || isSubmitted;
                                
                                if (!isAvailable) {
                                    return (
                                        <Chip
                                            label={status.status === 'upcoming' ? 'Coming Soon' : 'Assessment Closed'}
                                            color={status.color}
                                            size="medium"
                                            sx={{ fontWeight: 500, width: '100%', justifyContent: 'center' }}
                                        />
                                    );
                                }
                                
                                return (
                                    <Button
                                        onClick={() => handleTakeAssessment(assessment)}
                                        variant="contained"
                                        startIcon={isSubmitted ? <CheckCircle /> : <EditNote />}
                                        fullWidth
                                        sx={{
                                            background: 'linear-gradient(45deg, #0a0068 30%, #1a1078 90%)',
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            py: 1
                                        }}
                                    >
                                        {isSubmitted ? 'View Report' : 'Take Assessment'}
                                    </Button>
                                );
                            })()
                        ) : (
                            <>
                                <Button
                                    onClick={() => handleEdit(assessment)}
                                    variant="outlined"
                                    startIcon={<Edit />}
                                    size="small"
                                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                                >
                                    Edit
                                </Button>
                                <Button
                                    onClick={() => handlePreviewOpen(assessment)}
                                    variant="outlined"
                                    startIcon={<Preview />}
                                    size="small"
                                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                                >
                                    Preview
                                </Button>
                                {!isMobile && (
                                    <>
                                        <Button
                                            onClick={() => handleViewSubmissions(assessment)}
                                            variant="outlined"
                                            startIcon={<ViewList />}
                                            size="small"
                                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                                        >
                                            Submissions
                                        </Button>
                                        <Button
                                            onClick={() => handleDeleteAssignment(assessment.form_id)}
                                            variant="outlined"
                                            startIcon={<DeleteIcon />}
                                            size="small"
                                            color="error"
                                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                                        >
                                            Delete
                                        </Button>
                                    </>
                                )}
                                {isMobile && (
                                    <Box display="flex" gap={1} mt={1} width="100%">
                                        <Button
                                            onClick={() => handleViewSubmissions(assessment)}
                                            variant="outlined"
                                            startIcon={<ViewList />}
                                            size="small"
                                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500, flex: 1 }}
                                        >
                                            Submissions
                                        </Button>
                                        <Button
                                            onClick={() => handleDeleteAssignment(assessment.form_id)}
                                            variant="outlined"
                                            startIcon={<DeleteIcon />}
                                            size="small"
                                            color="error"
                                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500, flex: 1 }}
                                        >
                                            Delete
                                        </Button>
                                    </Box>
                                )}
                            </>
                        )}
                    </Box>
                </CardActions>
            </Card>
        </Fade>
    );

    return (
        <Box sx={{ py: { xs: 1, md: 3 } }}>
            {/* Header */}
            {/* <Box mb={3} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography
                        variant="h4"
                        sx={{
                            fontFamily: 'Poppins',
                            fontWeight: 600,
                            color: '#0a0068',
                            mb: 1,
                            fontSize: { xs: '1.2rem', md: '1.75rem' }
                        }}
                    >
                        Assessments
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {userData.user_role === "User" ? "Take your assessments and track your progress" : "Manage and review assessment submissions"}
                    </Typography>
                </Box>
                
                <Box sx={{ minWidth: 300, maxWidth: 400 }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search assessments..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: 'white',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    transform: 'translateY(-1px)'
                                },
                                '&.Mui-focused': {
                                    transform: 'translateY(-1px)',
                                    borderColor: '#0a0068',
                                    '& fieldset': {
                                        borderColor: '#0a0068 !important'
                                    }
                                }
                            },
                            '& .MuiInputBase-input': {
                                fontSize: '0.85rem',
                                fontWeight: 500
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ 
                                        fontSize: 18, 
                                        color: searchTerm ? '#0a0068' : 'text.secondary',
                                        transition: 'color 0.2s ease'
                                    }} />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm && (
                                <InputAdornment position="end">
                                    <Tooltip title="Clear search">
                                        <IconButton 
                                            size="small" 
                                            onClick={() => setSearchTerm('')}
                                            sx={{ 
                                                width: 24, 
                                                height: 24,
                                                color: '#cc4049',
                                                '&:hover': {
                                                    color: '#cc4049',
                                                    backgroundColor: 'rgba(204, 64, 73, 0.1)',
                                                    transform: 'scale(1.1)'
                                                }
                                            }}
                                        >
                                            <Clear sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            )
                        }}
                    />
                    {searchTerm && (
                        <Fade in={!!searchTerm}>
                            <Typography variant="caption" sx={{ 
                                fontSize: '0.7rem', 
                                color: '#6c757d', 
                                mt: 0.5,
                                display: 'block',
                                textAlign: 'right'
                            }}>
                                {filteredAssessments.length} of {assessments.length} assessments
                            </Typography>
                        </Fade>
                    )}
                </Box>
            </Box> */}

            {isLoading ? (
                <LoadingScreen message="Loading assessments..." />
            ) : (
                <>
                    {/* Mobile/Tablet View */}
                    {(isMobile || isTablet) ? (
                        <Box sx={{ px: { xs: 1, md: 3 } }}>
                            {filteredAssessments.length === 0 ? (
                                <Card sx={{ textAlign: 'center', py: 8, borderRadius: 3 }}>
                                    <CardContent>
                                        <Assignment sx={{ fontSize: 80, color: '#e0e0e0', mb: 2 }} />
                                        <Typography variant="h6" color="text.secondary" gutterBottom>
                                            {searchTerm ? 'No matching assessments found' : 'No assessments found'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {searchTerm ? 'Try adjusting your search criteria' : 'Check back later for new assessments'}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            ) : (
                                filteredAssessments.map((assessment, index) => (
                                    <MobileAssessmentCard key={assessment.id} assessment={assessment} index={index} />
                                ))
                            )}
                        </Box>
                    ) : (
                        /* Desktop Table View using CommonTable */
                        <AssessmentTableCommon
                            tableData={filteredAssessments}
                            userData={userData}
                            onEdit={(assessment) => onEdit(1, assessment)}
                            onDelete={handleDeleteAssignment}
                            onPreview={handlePreviewOpen}
                            onTakeAssessment={handleTakeAssessment}
                            onViewSubmissions={handleViewSubmissions}
                            isLoading={false}
                            getAssessmentStatus={getAssessmentStatus}
                            isAssessmentSubmitted={isAssessmentSubmitted}
                            getDisplayDate={getDisplayDate}
                            getDurationDisplay={getDurationDisplay}
                        />

                    )}

                    {/* Dialogs */}
                    <Dialog
                        open={isPreviewOpen}
                        onClose={handlePreviewClose}
                        fullWidth
                        maxWidth="md"
                        PaperProps={{
                            sx: { borderRadius: 3 }
                        }}
                    >
                        <DialogTitle sx={{
                            color: "#0a0068",
                            fontWeight: 700,
                            borderLeft: "4px solid #0a0068",
                            background: 'linear-gradient(135deg, #f8f9ff 0%, #e8ebff 100%)'
                        }}>
                            <Assignment sx={{ mr: 1, verticalAlign: 'middle' }} />
                            {selectedAssessment ? selectedAssessment.assignment_name : "Assessment Preview"}
                        </DialogTitle>
                        <DialogContent sx={{ borderLeft: "4px solid #0a0068" }}>
                            <AssessmentPreview assessment={selectedAssessment} />
                        </DialogContent>
                        <DialogActions sx={{ borderLeft: "4px solid #0a0068", p: 3 }}>
                            <Button
                                onClick={handlePreviewClose}
                                variant="outlined"
                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                            >
                                Close
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* <Dialog 
                        open={takeAssessmentOpen} 
                        onClose={handleTakeAssessmentClose} 
                        fullWidth 
                        maxWidth="md"
                        PaperProps={{
                            sx: { borderRadius: 3 }
                        }}
                    >
                        <DialogTitle sx={{ 
                            color: "#0a0068", 
                            fontWeight: 700, 
                            borderLeft: "4px solid #0a0068",
                            background: 'linear-gradient(135deg, #f8f9ff 0%, #e8ebff 100%)'
                        }}>
                            <Assignment sx={{ mr: 1, verticalAlign: 'middle' }} />
                            {selectedAssessment ? selectedAssessment.assignment_name : "Assessment"}
                        </DialogTitle>
                        <DialogContent sx={{ borderLeft: "4px solid #0a0068" }}> */}
                    {takeAssessmentOpen &&
                        <UserAssessmentForm
                            assessment={selectedAssessment}
                            userExist={isFormAlreadySubmitted}
                            takeAssessmentOpen={takeAssessmentOpen}
                            handleTakeAssessmentClose={handleTakeAssessmentClose}
                        />
                    }
                    {/* </DialogContent>
                        <DialogActions sx={{ borderLeft: "4px solid #0a0068", p: 3 }}>
                            <Button 
                                onClick={handleTakeAssessmentClose} 
                                variant="outlined"
                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                            >
                                Close
                            </Button>
                        </DialogActions>
                    </Dialog> */}

                    <Dialog
                        open={isSubmissionsOpen}
                        onClose={handleSubmissionsClose}
                        fullWidth
                        maxWidth="lg"
                        PaperProps={{
                            sx: { borderRadius: 3 }
                        }}
                    >
                        <DialogTitle sx={{
                            color: "#0a0068",
                            fontWeight: 700,
                            borderLeft: "4px solid #0a0068",
                            background: 'linear-gradient(135deg, #f8f9ff 0%, #e8ebff 100%)'
                        }}>
                            <ViewList sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Submissions for {selectedAssessment ? selectedAssessment.assignment_name : ""}
                        </DialogTitle>
                        <DialogContent sx={{ borderLeft: "4px solid #0a0068" }}>
                            <AssessmentSubmissions 
                                assessment={selectedAssessment} 
                                handleRefresh={() => selectedAssessment && handleViewSubmissions(selectedAssessment)} 
                            />
                        </DialogContent>
                        <DialogActions sx={{ borderLeft: "4px solid #0a0068", p: 3 }}>
                            <Button
                                onClick={handleSubmissionsClose}
                                variant="outlined"
                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                            >
                                Close
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
            )}
        </Box>
    );
};

export default AssessmentTable;